// Extrait du proto Modules/12M Cash/treso_v9.jsx (lignes 447-540).
// VERBATIM — décomposition mensuelle masse salariale + 13e + primes + jetons.

import { MOIS_COURT } from './constants';
import { calToHorizon } from './helpers';

/**
 * calcSalaires — produit la décomposition mensuelle propre :
 *   base[H]      : brut récurrent + variations permanentes (cumulées dès le mois)
 *   treizieme[H] : 13e mois (uniquement sur Décembre calendaire si dans l'horizon)
 *   primes[H]    : primes & bonus (au mois calendaire saisi)
 *   jetons[H]    : jetons de présence (au mois calendaire saisi)
 *   variable[H]  : primes + jetons (= primes[m] + jetons[m])
 *   brut[H]      : base + treizieme + variable (masse réellement versée)
 *   masseDeclaree : base déclarée pour les assurances (constante mensuelle)
 *   moisLabels[H] : libellés "M YYYY" pour l'axe X
 *   labels : { J F M A ... } courts par index horizon
 *
 * Agrégats annuels :
 *   sumBase, sumTreizieme, sumVariable, sumBrut
 *
 * Hypothèse stricte : variations.mois = index dans l'horizon (sémantique conservée).
 * Hypothèse stricte : primes/jetons.mois = mois calendaire (0-11).
 */
export function calcSalaires(scenario, H) {
  const horizon = H || scenario?.horizon || 12;
  const moisDebut = Number(scenario?.moisDebut) || 0;
  const anneeDebut = Number(scenario?.anneeDebut) || new Date().getFullYear();
  const salaireBrutMensuel = Number(scenario?.salaireBrutMensuel) || 0;
  const nbMois = Number(scenario?.nbMoisSalaire) === 13 ? 13 : 12;

  // 13e mois : index horizon de Décembre (null si hors plage)
  const idxDecembre = nbMois === 13 ? calToHorizon(11, moisDebut, horizon) : null;

  // Variations permanentes (mois = index horizon)
  const cumulDelta = (mIdx) => (scenario?.variations || [])
    .filter(v => Number(v.mois) <= mIdx)
    .reduce((acc, v) => acc + (Number(v.montantMensuel) || 0), 0);

  const base = Array.from({ length: horizon }, (_, m) => salaireBrutMensuel + cumulDelta(m));

  // 13e mois : prorata sur l'année civile (jan-déc).
  // Pour les mois antérieurs au démarrage du planning : brut initial (sans variations).
  // Pour les mois inclus dans l'horizon : base[mIdx] (avec variations cumulées).
  // Le 13e versé en décembre = somme des 12 bases calendaires / 12.
  const treizieme = Array(horizon).fill(0);
  if (idxDecembre !== null) {
    let sommeAnnuelle = 0;
    for (let moisCal = 0; moisCal < 12; moisCal++) {
      const idxH = calToHorizon(moisCal, moisDebut, horizon);
      // Si le mois calendaire tombe avant ou en décembre dans l'horizon
      // ET qu'il est ≤ idxDecembre (= dans l'année civile en cours), on prend la base réelle
      if (idxH !== null && idxH <= idxDecembre) {
        sommeAnnuelle += base[idxH];
      } else {
        // Mois antérieur au démarrage : brut initial (sans variations)
        sommeAnnuelle += salaireBrutMensuel;
      }
    }
    treizieme[idxDecembre] = sommeAnnuelle / 12;
  }

  // Primes & bonus / Jetons : mois calendaire → index horizon
  const primes = Array(horizon).fill(0);
  (scenario?.primesBonus || []).forEach(p => {
    const idx = calToHorizon(Number(p.mois), moisDebut, horizon);
    if (idx !== null) primes[idx] += Number(p.montant) || 0;
  });
  const jetons = Array(horizon).fill(0);
  (scenario?.jetons || []).forEach(j => {
    const idx = calToHorizon(Number(j.mois), moisDebut, horizon);
    if (idx !== null) jetons[idx] += Number(j.montant) || 0;
  });

  const variable = Array.from({ length: horizon }, (_, m) => primes[m] + jetons[m]);
  const brut = Array.from({ length: horizon }, (_, m) => base[m] + treizieme[m] + variable[m]);

  // Libellés
  const moisLabels = Array.from({ length: horizon }, (_, m) => {
    const moisIdx = (moisDebut + m) % 12;
    const annee = anneeDebut + Math.floor((moisDebut + m) / 12);
    return `${MOIS_COURT[moisIdx]} ${annee}`;
  });
  const labelsCourts = Array.from({ length: horizon }, (_, m) => MOIS_COURT[(moisDebut + m) % 12]);

  // Agrégats
  const sumBase = base.reduce((a, b) => a + b, 0);
  const sumTreizieme = treizieme.reduce((a, b) => a + b, 0);
  const sumVariable = variable.reduce((a, b) => a + b, 0);
  const sumBrut = sumBase + sumTreizieme + sumVariable;

  return {
    horizon, moisDebut, anneeDebut,
    base, treizieme, primes, jetons, variable, brut,
    salaireBrutMensuel, nbMois,
    moisLabels, labelsCourts,
    idxDecembre,
    sumBase, sumTreizieme, sumVariable, sumBrut,
  };
}
